import { useCallback } from 'react';

import { getCurrency } from '@/domains/core/settings/utils';
import { algoliaProductToPrProductMapper } from '@/domains/core/tracking/utils/algoliaProductToPrProductMapper';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import {
  UseFingerprintReconciliationTrackClickItem,
  UseFingerprintReconciliationTrackDisplayItem,
  useFingerprintReconciliationTracking,
} from '@/domains/productDiscovery/Recommendation/hooks/useFingerprintReconciliationTracking';
import type { Product } from '@/productDiscovery/Recommendation/interfaces/product';

type Params = {
  engineName: string;
  playlistLocation: string;
  playlistName?: string;
};

export const useRecommendationPlaylistTracking = ({
  engineName,
  playlistLocation,
  playlistName = '',
}: Params) => {
  const trackDisplayedItem = useCallback<
    UseFingerprintReconciliationTrackDisplayItem<Product>
  >(
    ({ itemsWithIndex, reconciliationData }) => {
      const sponsoredProductsNumber = itemsWithIndex.filter(
        ({ item }) => item.isSponsored,
      ).length;

      const event = {
        amp_event_name: 'Display playlist',
        event: 'interaction_view_item_list',
        event_trigger: playlistLocation,
        number_sp_display: sponsoredProductsNumber,
        page_type: playlistLocation.includes('cart') ? 'cart' : 'product',
        playlist_type: sponsoredProductsNumber > 0 ? 'hybrid' : 'natural',
        playlist_name: playlistName,
        playlist_reconciliation: reconciliationData.playlistReconciliation,
        pr_products: itemsWithIndex.map(({ item, itemIndex }) =>
          algoliaProductToPrProductMapper(item, itemIndex),
        ),
        reco_engine_name: engineName,
      };

      Gtm.push(event);
    },
    [playlistLocation, playlistName, engineName],
  );

  const trackClickedItem = useCallback<
    UseFingerprintReconciliationTrackClickItem<Product>
  >(
    ({ itemIndex, item, reconciliationData }) => {
      const event = {
        amp_event_name: 'Click on product card',
        currency: getCurrency(),
        event_trigger: playlistLocation,
        event: 'interaction_product click',
        has_bulk_label: item.hasBulk,
        mgr: undefined,
        page_type: playlistLocation.includes('cart') ? 'cart' : 'product',
        playlist_name: playlistName,
        playlist_reconciliation: reconciliationData?.playlistReconciliation,
        pr_products: algoliaProductToPrProductMapper(item, itemIndex),
        // B2B only
        has_a_highlighted_product: undefined,
        has_promo_product: item.hasPromoLabel,
        mf_product_count: item.isMmf ? 1 : 0,
        pro_product_count: item.hasProConditions ? 1 : 0,
      };

      Gtm.push(event);
    },
    [playlistLocation, playlistName],
  );

  const data = useFingerprintReconciliationTracking({
    engineTitle: engineName || '',
    eventTrigger: playlistLocation,
    trackDisplayedItem,
    trackClickedItem,
    visibleThreshold: 0.7,
  });

  return data;
};
